import i18n from 'helpers/i18n';
import messages from './messages';
import { useFormikContext, useField } from 'formik';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Combobox, ComboboxOption } from '@teachstonellc/component-library';
import { searchAccountsAutocomplete } from 'actions/accounts';
import { getUserAccounts } from 'actions/users';
import { Training } from 'types/api/Training';

const MIN_QUERY_LENGTH = 3;

export type Account = {
  id: number;
  name: string;
};

interface EditTraining extends Training {
  account_id: number;
}

interface SearchAccountProps {
  /** Select input name. */
  name: string;

  /** Select input label. */
  label: string;

  /** Initial account if there's an existing selection. */
  initialAccount?: Account;

  /** Selected eligible trainer ID. */
  trainerId?: number | null;

  /** Selected trainer type ID. */
  selectedTrainerTypeId?: number | null;

  /** Available account options. When provided, remote autocomplete is disabled. */
  availableAccounts?: Account[];
}

function accountOption(account?: Account): ComboboxOption | null {
  return account ? { label: account.name, value: account.id } : null;
}

function SearchAccount({
  name,
  label,
  initialAccount,
  trainerId,
  selectedTrainerTypeId,
  availableAccounts
}: SearchAccountProps) {
  const [, meta, { setValue: setAccountId, setTouched: setAccountTouched }] =
    useField(name);
  const [options, setOptions] = useState<ComboboxOption[]>(
    initialAccount ? [accountOption(initialAccount) as ComboboxOption] : []
  );
  const [selectedOption, setSelectedOption] = useState<ComboboxOption | null>(
    accountOption(initialAccount)
  );
  const [query, setQuery] = useState(initialAccount?.name ?? '');
  const isFirstRender = useRef(true);
  const availableAccountsInitialized = useRef(false);
  const previousAccountId = useRef<unknown>(initialAccount?.id);
  const { values, setFieldValue } = useFormikContext<EditTraining>();
  const availableOptions = useMemo(
    () => availableAccounts?.map(account => accountOption(account)!),
    [availableAccounts]
  );

  const resetNodeId = useCallback(
    () => setFieldValue('node_id', null),
    [setFieldValue]
  );

  useEffect(() => {
    if (
      values.id ||
      availableAccountsInitialized.current ||
      availableAccounts?.length !== 1
    ) {
      return;
    }

    availableAccountsInitialized.current = true;

    if (meta.value) {
      return;
    }

    const [account] = availableAccounts;
    const option = accountOption(account);

    setSelectedOption(option);
    setQuery(account.name);
    setAccountId(account.id);
    resetNodeId();
  }, [meta.value, availableAccounts, resetNodeId, setAccountId, values.id]);

  useEffect(() => {
    function getSuggestedTrainerAccount() {
      const EMPLOYEE_TYPE_ID = 2;

      if (availableAccounts) {
        return;
      }

      if (values.id && isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      if (trainerId && selectedTrainerTypeId !== EMPLOYEE_TYPE_ID) {
        getUserAccounts(trainerId).then(response => {
          if (response.data.length > 0) {
            const suggestedTrainerAccount = response.data[0];
            const option = {
              label: suggestedTrainerAccount.account_name,
              value: suggestedTrainerAccount.account_id
            };

            setSelectedOption(option);
            setQuery(option.label);
            setAccountId(option.value);
            resetNodeId();
          }
        });
      } else if (selectedTrainerTypeId === 2) {
        setSelectedOption(null);
        setQuery('');
        setAccountId(null);
        resetNodeId();
      }
    }

    getSuggestedTrainerAccount();
  }, [
    availableAccounts,
    trainerId,
    values.id,
    setAccountId,
    resetNodeId,
    selectedTrainerTypeId
  ]);

  // Keep the displayed account in sync when Formik resets the form.
  useEffect(() => {
    if (previousAccountId.current && !meta.value) {
      setSelectedOption(null);
      setQuery('');
    }

    previousAccountId.current = meta.value;
  }, [meta.value]);

  async function searchAutocomplete(searchQuery: string) {
    const { data } = await searchAccountsAutocomplete(searchQuery);

    setOptions(
      data.accounts.map((item: any) => ({
        label: item.name,
        value: item.id
      }))
    );
  }

  function handleQueryChange(newQuery: string) {
    setQuery(newQuery);

    if (availableAccounts) {
      return;
    }

    if (newQuery.length >= MIN_QUERY_LENGTH) {
      searchAutocomplete(newQuery);
    }
  }

  function handleChange(option: ComboboxOption | null) {
    const accountChanged = option?.value !== meta.value;

    setSelectedOption(option);
    setQuery(option?.label ?? '');
    setAccountId(option?.value ?? null);

    if (accountChanged) {
      resetNodeId();
    }
  }

  const isError = Boolean(meta.touched && meta.error);

  return (
    <div onBlur={() => setAccountTouched(true)}>
      <Combobox
        label={label}
        placeholder={i18n.ft(messages.search)}
        options={availableOptions ?? options}
        selectedOption={selectedOption}
        onChange={handleChange}
        query={query}
        setQuery={handleQueryChange}
        showDropdownIcon
        showSearchIcon={false}
        maxResults={100}
        immediate={Boolean(availableAccounts)}
        clearable
        error={isError ? meta.error : undefined}
      />
    </div>
  );
}

export default SearchAccount;
